<template>
  <div>
    <div :class="containerClass">
      <div class="back-result-lg font-weight-bold mt-5">
        <a href="#" @click.prevent="goBack()" style="text-decoration: underline" class="mr-2">
          <span class="material-icons icon-32pt">navigate_before</span>
        </a>
      </div>

      <div v-if="isExternalSponsorship">
        <b-skeleton-wrapper :loading="isLoading">
          <template #loading>
            <h3 class="mt-3"><b-skeleton width="45%"></b-skeleton></h3>
            <b-skeleton width="30%"></b-skeleton>
            <div class="row mt-5">
              <div class="col-lg-8">
                <b-skeleton width="100%"></b-skeleton>
                <b-skeleton width="90%"></b-skeleton>
                <b-skeleton width="95%"></b-skeleton>
                <b-skeleton width="60%"></b-skeleton>
              </div>
              <div class="col-lg-4"><b-skeleton-img height="220px" no-aspect></b-skeleton-img></div>
            </div>
          </template>

          <div class="sponsorship-detail">
            <div class="d-flex flex-wrap justify-content-between align-items-start">
              <h1 class="sponsorship-title mb-0 mr-3">{{ get(scholarship, 'title') }}</h1>

              <b-btn
                v-if="applyNowRedirectLink"
                variant="primary"
                class="btn-normal text-uppercase"
                :href="getClickableLink(applyNowRedirectLink)"
              >
                Apply Now
              </b-btn>
            </div>

            <div class="d-flex flex-wrap justify-content-between align-items-center mt-2">
              <div class="sponsorship-meta">
                <span class="text-muted mr-1">Offered By</span>
                <span class="font-weight-bold">{{ scholarshipProviderName }}</span>
                <span v-for="meta in metaItems" :key="meta"> &middot; {{ meta }} </span>
              </div>

              <div v-if="get(scholarship, 'phone_number')" class="sponsorship-meta">
                <span class="text-muted mr-1">Call</span>
                <a :href="`tel:${get(scholarship, 'phone_number')}`">{{ get(scholarship, 'phone_number') }}</a>
              </div>
            </div>

            <div class="sponsorship-tabs mt-4">
              <a
                v-for="tab in availableTabs"
                :key="tab.key"
                href="#"
                class="sponsorship-tab"
                :class="{ active: activeTab === tab.key }"
                @click.prevent="activeTab = tab.key"
                >{{ tab.label }}</a
              >
            </div>

            <div class="row mt-4">
              <div class="col-lg-8">
                <div v-if="activeTabContent" class="sponsorship-content" v-html="activeTabContent"></div>
                <div v-else class="text-muted">This information is not provided for this sponsorship yet.</div>
              </div>

              <div class="col-lg-4" v-if="summaryItems.length">
                <div class="sponsorship-summary">
                  <div v-for="item in summaryItems" :key="item.label" class="sponsorship-summary-item">
                    <div class="sponsorship-summary-label">{{ item.label }}</div>
                    <div class="sponsorship-summary-value">{{ item.value }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-skeleton-wrapper>
      </div>

      <template v-else>

      <div v-if="isPublic" class="d-flex justify-content-center mb-3">
        <img
          class="mr-2"
          :src="
            get(scholarship, 'posted_by_org.logo_url')
              ? get(scholarship, 'posted_by_org.logo_url')
              : get(scholarship, 'employers[0].logo_url') || DefaultAvatar
          "
          width="150px"
          height="auto"
        />
        <div style="font-size: 2rem" :class="{ 'd-flex align-items-center ': isPublic }">
          <b :class="{ 'text-center': isPublic }">{{ get(scholarship, 'title') }} </b>
        </div>
      </div>

      <div style="font-size: 2rem" v-else>
        <b>{{ get(scholarship, 'title') }} </b>
      </div>

      <div class="mt-2" :class="{ 'd-flex justify-content-center': isPublic }" style="font-size: 1.1rem">
        <b-skeleton-wrapper :loading="isLoading" class="w-100">
          <template #loading>
            <div :class="{ 'd-flex justify-content-center': isPublic }">
              <b-skeleton width="35%"></b-skeleton>
            </div>
          </template>
          <span class="mr-1">Offered By</span
          ><b class="text-dark">{{ scholarshipProviderName }}</b>
        </b-skeleton-wrapper>
      </div>

      <page-separator title="" />
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <div class="row">
            <div class="col-lg-2">
              <div class="d-flex justify-content-center">
                <a
                  href="#"
                  @click.prevent
                  class="avatar avatar-xxl overlay js-overlay overlay--primary rounded-circle p-relative o-hidden mb-16pt align-self-center"
                  style="border: 1px solid #dfe2e6;id #dfe2e6;id #dfe2e6;pxl"
                >
                  <b-skeleton-img class="avatar-img" width="160" height="100%"></b-skeleton-img>
                </a>
              </div>

              <div class="mb-3">
                <b-skeleton type="button" width="100%"></b-skeleton>
              </div>
            </div>

            <div class="col-lg-10 d-flex mt-3">
              <div class="flex" style="max-width: 100%">
                <h3><b-skeleton width="100%"></b-skeleton></h3>
                <div class="mb-0 mt-3" style="text-transform: none">
                  <b-skeleton width="100%"></b-skeleton>
                  <b-skeleton width="55%"></b-skeleton>
                  <b-skeleton width="70%"></b-skeleton>
                  <b-skeleton width="65%"></b-skeleton>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div class="row">
          <div class="col-lg-2">
            <div class="d-flex justify-content-center">
              <a
                href="#"
                @click.prevent
                class="avatar avatar-xxl overlay js-overlay overlay--primary rounded-circle p-0 o-hidden d-flex justify-content-center align-items-center mb-3"
                style="border: 1px solid #dfe2e6;id #dfe2e6;id #dfe2e6;pxl"
              >
                <img
                  class="avatar-img m-0"
                  :src="get(scholarship, 'image_url') || DefaultAvatar"
                  width="160"
                  height="auto"
                />
              </a>
            </div>

            <!-- :to="{
                  name: 'program-detail',
                  params: { slug: this.$route.query.program },
                  query: { tab: 'upcoming-classes' },
                }" -->
            <div class="mb-3">
              <b-btn
                v-if="get(scholarship, 'scholarship_type') === SPONSORSHIP_SUBMISSION_TYPES.IN_APP_SUBMISSION"
                variant="light"
                :disabled="isLoading"
                @click.prevent="() => (showTermsModal = true)"
                class="mt-2 w-100 mr-4 btn-normal"
              >
                <md-icon class="icon-16pt mr-1">launch</md-icon>Apply Now
              </b-btn>
              <b-btn
                v-else-if="applyNowRedirectLink"
                variant="light"
                :disabled="isLoading"
                class="mt-2 w-100 mr-4 btn-normal"
                :href="getClickableLink(applyNowRedirectLink)"
              >
                <md-icon class="icon-16pt mr-1">launch</md-icon>Apply Now
              </b-btn>
            </div>
          </div>

          <div class="col-lg-10 d-flex">
            <div class="flex" style="max-width: 100%">
              <div class="mb-0">
                <div
                  class="mb-1"
                  style="font-size: 15px"
                  v-if="get(scholarship, 'min_amount') && get(scholarship, 'max_amount')"
                >
                  <strong class="text-primary mr-1">Amount: </strong>
                  ${{ $n(get(scholarship, 'min_amount')) }} - ${{ $n(get(scholarship, 'max_amount')) }}
                </div>

                <div class="mb-1" style="font-size: 15px" v-if="get(scholarship, 'deadline')">
                  <strong class="text-primary mr-1">Deadline: </strong
                  >{{ formatDateSimple(get(scholarship, 'deadline')) }}
                </div>

                <div class="mb-1" style="font-size: 15px">
                  <strong class="text-primary mr-1">Category: </strong
                  >{{ $t(`sponsorshipCategories.${scholarship.categories}`) }}
                </div>

                <div class="mb-1" style="font-size: 15px" v-if="get(scholarship, 'ext_sponsorship_type')">
                  <strong class="text-primary mr-1">Sponsorship Type: </strong
                  >{{ formatExtSponsorshipType(get(scholarship, 'ext_sponsorship_type')) }}
                </div>

                <div
                  class="mb-1"
                  v-if="
                    get(scholarship, 'address') ||
                    get(scholarship, 'region') ||
                    get(scholarship, 'city') ||
                    get(scholarship, 'state') ||
                    get(scholarship, 'zipcode') ||
                    get(scholarship, 'country')
                  "
                  style="font-size: 15px"
                >
                  <strong class="text-primary mr-1">Address: </strong>
                  {{
                    computeAddress(
                      scholarship,
                      ['address', 'region', 'city', 'state', 'zipcode', 'country'],
                      (stateKey = 'state'),
                      (zipcodeKey = 'zipcode')
                    )
                  }}
                </div>

                <div class="mb-1" v-if="get(scholarship, 'work_commitment')" style="font-size: 15px">
                  <strong class="text-primary mr-1">Work Commitment: </strong
                  >{{ get(scholarship, 'work_commitment') }} Months
                </div>

                <div class="mb-1" v-if="get(scholarship, 'type')" style="font-size: 15px">
                  <strong class="text-primary mr-1">Type: </strong
                  >{{ $t(`jobWorkTypeOpts.${get(scholarship, 'type')}`) }}
                </div>

                <div class="mb-1" v-if="get(scholarship, 'schedule')" style="font-size: 15px">
                  <strong class="text-primary mr-1">Schedule: </strong
                  >{{ $t(`jobScheduleOpts.${get(scholarship, 'schedule')}`) }}
                </div>

                <div
                  v-if="get(scholarship, 'minimum_pay') && get(scholarship, 'maximum_pay')"
                  class="mb-1"
                  style="font-size: 15px"
                >
                  <strong class="text-primary mr-1">Pay: </strong>
                  ${{ $n(get(scholarship, 'minimum_pay')) }} - ${{ $n(get(scholarship, 'maximum_pay')) }}
                </div>

                <div v-if="get(scholarship, 'pay_rate')" class="mb-1" style="font-size: 15px">
                  <strong class="text-primary mr-1">Pay Rate: </strong
                  >{{ $t(`jobPayRateOpts.${get(scholarship, 'pay_rate')}`) }}
                </div>

                <div v-if="get(scholarship, 'benefits.length')" class="mb-1" style="font-size: 15px">
                  <strong class="text-primary mr-1">Benefits: </strong>
                  <span v-for="(benefit, index) in get(scholarship, 'benefits')" :key="benefit + index">
                    <span
                      v-if="
                        get(scholarship, 'benefits.length') >= 2 && get(scholarship, 'benefits.length') - 1 <= index
                      "
                    >
                      and</span
                    >
                    {{ benefit === 'other' ? get(scholarship, 'other_benefits') : $t(`benefitOptions.${benefit}`) }}
                    <span v-if="get(scholarship, 'benefits.length') - 2 > index">,</span>
                  </span>
                </div>

                <div
                  v-if="get(scholarship, 'career_offerings.length') || get(scholarship, 'other_career_offering')"
                  class="mb-1"
                  style="font-size: 15px"
                >
                  <strong class="text-primary mr-1">Career Offerings: </strong>
                  <span v-for="(career, index) in get(scholarship, 'career_offerings')" :key="career.name + index">
                    {{ career.name }}
                    <span v-if="get(scholarship, 'career_offerings.length') - 1 > index">,</span>
                  </span>
                  <span v-if="get(scholarship, 'career_offerings.length') > 1">and</span>

                  {{ get(scholarship, 'other_career_offering') }}
                </div>

                <div v-if="get(scholarship, 'career_offerings_details')" class="mb-1" style="font-size: 15px">
                  <strong class="text-primary mr-1">Career Offerings Details: </strong
                  >{{ get(scholarship, 'career_offerings_details') }}
                </div>

                <div v-if="get(scholarship, 'description')" class="mt-3">
                  <h5 class="mb-2 text-primary">Description</h5>
                  <div class="p-3" style="font-size: 15px; border: 1px solid #e6e9ec; border-radius: 8px">
                    <div v-html="get(scholarship, 'description')"></div>
                  </div>
                </div>
                <div v-else class="mt-3 text-muted" style="font-size: 15px">
                  Description is not provided for this sponsorship yet.
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-skeleton-wrapper>
      </template>
    </div>
    <app-terms-modal
      :show-modal="showTermsModal"
      @close="showTermsModal = false"
      :scholarship="scholarship"
    ></app-terms-modal>
  </div>
</template>


<script>
import { get, map } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Page from '@/components/Page.vue';
import DefaultAvatar from '@/assets/images/logos/logo_icon.png';
import { formatDateSimple, getClickableLink, computeAddress } from '@/common/utils';
import { SPONSORSHIP_SUBMISSION_TYPES, SPONSORSHIP_CATEGORIES_TYPES } from '@/common/constants';
import AppTermsModal from './AppTermsModal.vue';

export default {
  name: 'SchDetails',

  components: {
    PageSeparator,
    AppTermsModal,
  },
  extends: Page,

  props: {
    isPublic: { type: Boolean, default: false },
  },

  data() {
    return {
      title: 'Sponsorship Details',
      DefaultAvatar,
      isLoading: false,
      isStatusUpdating: false,
      scholarship: {},
      showTermsModal: false,
      activeTab: 'description',
      SPONSORSHIP_SUBMISSION_TYPES,
      SPONSORSHIP_CATEGORIES_TYPES,
    };
  },

  computed: {
    ...mapGetters('school', ['getCurrentSchool']),
    ...mapGetters('auth', ['getLoggedInUser']),

    isExternalSponsorship() {
      // While fetching, the scholarship is empty, so fall back to the route hint to
      // avoid flashing the non-external skeleton on an external sponsorship.
      if (this.isLoading) {
        return this.$route.query.source === 'external';
      }

      return get(this.scholarship, 'scholarship_type') === 'by_superadmin';
    },

    metaItems() {
      return [
        get(this.scholarship, 'categories') ? this.$t(`sponsorshipCategories.${this.scholarship.categories}`) : null,
        this.formatExtSponsorshipType(get(this.scholarship, 'ext_sponsorship_type')),
        get(this.scholarship, 'country'),
      ].filter((item) => !!item);
    },

    availableTabs() {
      return [
        { key: 'description', label: 'Description', content: get(this.scholarship, 'description') },
        { key: 'who_qualifies', label: 'Who Qualifies', content: get(this.scholarship, 'who_qualifies') },
        { key: 'requirements', label: 'Requirements', content: get(this.scholarship, 'requirements') },
        { key: 'how_to_apply', label: 'How to Apply', content: get(this.scholarship, 'how_to_apply') },
      ].filter((tab) => tab.key === 'description' || !!tab.content);
    },

    activeTabContent() {
      return get(
        this.availableTabs.find((tab) => tab.key === this.activeTab),
        'content'
      );
    },

    summaryItems() {
      const items = [];

      if (get(this.scholarship, 'min_amount') && get(this.scholarship, 'max_amount')) {
        items.push({
          label: 'Funding',
          value: `$${this.$n(this.scholarship.min_amount)} - $${this.$n(this.scholarship.max_amount)}`,
        });
      }

      if (get(this.scholarship, 'deadline')) {
        items.push({ label: 'Deadline', value: formatDateSimple(this.scholarship.deadline) });
      }

      if (get(this.scholarship, 'work_commitment')) {
        items.push({ label: 'Work Commitment', value: `${this.scholarship.work_commitment} Months` });
      }

      if (get(this.scholarship, 'type')) {
        items.push({ label: 'Type', value: this.$t(`jobWorkTypeOpts.${this.scholarship.type}`) });
      }

      if (get(this.scholarship, 'schedule')) {
        items.push({ label: 'Schedule', value: this.$t(`jobScheduleOpts.${this.scholarship.schedule}`) });
      }

      if (get(this.scholarship, 'minimum_pay') && get(this.scholarship, 'maximum_pay')) {
        items.push({
          label: 'Pay',
          value: `$${this.$n(this.scholarship.minimum_pay)} - $${this.$n(this.scholarship.maximum_pay)}`,
        });
      }

      if (get(this.scholarship, 'pay_rate')) {
        items.push({ label: 'Pay Rate', value: this.$t(`jobPayRateOpts.${this.scholarship.pay_rate}`) });
      }

      const address = computeAddress(
        this.scholarship,
        ['address', 'region', 'city', 'state', 'zipcode', 'country'],
        'state',
        'zipcode'
      );
      if (address) {
        items.push({ label: 'Location', value: address });
      }

      return items;
    },

    scholarshipProviderName() {
      return (
        get(this.scholarship, 'external_organization') ||
        get(this.scholarship, 'posted_by_org.name') ||
        get(this.scholarship, 'employers[0].name') ||
        'None'
      );
    },

    applyNowRedirectLink() {
      if (get(this.scholarship, 'scholarship_type') === 'by_superadmin') {
        return get(this.scholarship, 'call_to_action') || get(this.scholarship, 'external_link') || null;
      }

      return get(this.scholarship, 'external_link') || null;
    },

    eligibleSchools() {
      return get(this.scholarship, 'schools') ? map(this.scholarship.schools, 'name').join(', ') : 'None';
    },
  },

  methods: {
    ...mapActions('program', ['getScholarship', 'getProgramFromSlug', 'ext_scholarships_from_transition_public']),
    ...mapActions('settings', ['setSettings']),
    get,
    getClickableLink,
    formatDateSimple,
    computeAddress,

    formatExtSponsorshipType(type) {
      const typeMap = {
        wioa: 'WIOA',
        government: 'Government',
      };

      if (!type) {
        return '';
      }

      return typeMap[type] || String(type).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    },

    goBack() {
      this.$router.go(-1);
    },

    async fetchExternalScholarshipById() {
      let programId = this.$route.query.program_id;

      if (!programId && this.$route.query.program) {
        const program = await this.getProgramFromSlug({ slug: this.$route.query.program });
        programId = get(program, 'id');
      }

      if (!programId) {
        return null;
      }

      const res = await this.ext_scholarships_from_transition_public({
        program_id: programId,
        is_active: true,
      });

      const externalScholarships = get(res, 'data.results', get(res, 'data', []));
      if (!Array.isArray(externalScholarships)) {
        return null;
      }

      return externalScholarships.find((item) => String(get(item, 'id')) === String(this.$route.params.id)) || null;
    },

    async fetchScholarship() {
      this.isLoading = true;
      try {
        let scholarshipData = null;
        const isExternalSource = this.$route.query.source === 'external';

        if (isExternalSource) {
          scholarshipData = await this.fetchExternalScholarshipById();
        }

        if (!scholarshipData) {
          try {
            const res = await this.getScholarship({ scholarship_id: this.$route.params.id });
            scholarshipData = get(res, 'data');
          } catch (error) {
            scholarshipData = await this.fetchExternalScholarshipById();
          }
        }

        if (!scholarshipData) {
          throw new Error('Sponsorship not found');
        }

        this.scholarship = scholarshipData;

        this.title = this.scholarship.title;
      } catch (e) {
        this.$router.go(-1);
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.setSettings({ layout: { layout: 'fixed' } });

    this.fetchScholarship();
  },
};
</script>

<style scoped>
.sponsorship-title {
  font-size: 2rem;
  font-weight: 700;
}

.sponsorship-meta {
  font-size: 0.9375rem;
}

.sponsorship-tabs {
  border-bottom: 1px solid #e6e9ec;
}

.sponsorship-tab {
  display: inline-block;
  padding: 0.875rem 0;
  margin-right: 2.25rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #6c757d;
  border-bottom: 3px solid transparent;
}

.sponsorship-tab:hover {
  color: #212529;
  text-decoration: none;
}

.sponsorship-tab.active {
  color: #212529;
  font-weight: 700;
  border-bottom-color: var(--primary, #b62c1e);
}

.sponsorship-content {
  font-size: 0.9375rem;
  line-height: 1.7;
}

.sponsorship-summary {
  background-color: #f5f6f7;
  border-radius: 6px;
  padding: 1.25rem;
}

.sponsorship-summary-item + .sponsorship-summary-item {
  margin-top: 1rem;
}

.sponsorship-summary-label {
  font-size: 0.8125rem;
  color: #868e96;
}

.sponsorship-summary-value {
  font-size: 0.9375rem;
  font-weight: 600;
}
</style>
